import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { toast } from "react-toastify";
import { AuthState, login, setVerifiedEmail } from "../features/auth/authSlice";
import { UserPermissions } from "../features/groups/groupSlice";
import {
  setTermsAndConditionsUpdated,
  setUser,
  setUserNotifications,
  updateUserSettings,
  User,
  UserSettings,
  toggleNotificationRead,
  markAllNotificationsAsRead,
} from "../features/user/userSlice";
import { LanguageText } from "../dictionary/UserText";

export type UserGroup = {
  group_id: string;
  permissions: UserPermissions;
  group_name: string;
};

export type Notification = {
  id: string;
  timestamp: string;
  content: string;
  read: boolean;
};

/*
API requests for user related data
*/
export const userAPI = createApi({
  reducerPath: "userAPI",
  //base query for all requests: token used in all requests added to header
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_BASE_URL,
    prepareHeaders: (headers: any, { getState }: any) => {
      const token = getState().auth.token;
      if (token) {
        headers.set("token", `${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["User", "Notifications"],
  endpoints: (builder) => ({
    //get user data - Takes token and returns user data
    getUserInfo: builder.query({
      query: () => {
        return {
          url: "/user",
        };
      },

      //sets the type of the success response
      transformResponse: (result: User) => result,
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          //on success, dispatch the user data to the user slice and the verified email to the auth slice
          const { data } = await queryFulfilled;
          console.log(" getUserInfo: builder.query");
          dispatch(
            setUser({
              username: data.username,
              email: data.email,
              phone_number: data.phone_number,
              id: data.id,
              groups: data.groups,
              email_verified: data.email_verified,
              terms_and_conditions_version: data.terms_and_conditions_version,
              settings: data.settings,
              latest_terms_accepted: data.latest_terms_accepted,
              notifications: data.notifications,
            })
          );
          setTermsAndConditionsUpdated(data.latest_terms_accepted ?? false);
          if (data.email_verified !== undefined && data.email_verified !== null) {
            dispatch(setVerifiedEmail(data.email_verified));
          }
        } catch (error) {
          //console.log(error);
        }
      },
      providesTags: ["User"],
    }),
    getUserNotifications: builder.query({
      query: () => {
        return {
          url: "/notifications/user",
          method: "GET",
        };
      },
      transformResponse: (result: any) => result,
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const {
            data: { notifications },
          } = await queryFulfilled;
          dispatch(setUserNotifications(notifications));
        } catch (error) {
          console.log("Something went wrong fetching notifications", error);
        }
      },
      providesTags: ["Notifications"],
    }),
    getGroupUserInfo: builder.query({
      query: ({ groupID, userID }: { groupID: string; userID: string }) => {
        return `/user/${groupID}/${userID}`;
      },
    }),
    toggleUserNotificationRead: builder.mutation({
      query: (args: { notificationID: string; language: LanguageText }) => {
        const { notificationID } = args;
        return {
          url: `/user/${notificationID}/readtoggle`,
          method: "PATCH",
        };
      },
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        const { notificationID, language } = args;
        dispatch(toggleNotificationRead(notificationID));
        try {
          await queryFulfilled;
        } catch (error) {
          dispatch(toggleNotificationRead(notificationID));
          toast.error(language?.notifications?.toast?.error);
        }
      },
      invalidatesTags: ["Notifications"],
    }),
    readAllNotifications: builder.mutation({
      query: () => {
        return {
          url: `/user/notifications/markalltrue`,
          method: "PATCH",
        };
      },
      async onQueryStarted(args, { dispatch, queryFulfilled, getState }) {
        const { language } = args;
        const previousNotifications = (getState() as any)?.user?.notifications;
        dispatch(markAllNotificationsAsRead());
        try {
          await queryFulfilled;
        } catch (error) {
          if (previousNotifications) {
            dispatch(setUserNotifications(previousNotifications));
          }
          toast.error(language?.notifications?.toast?.error);
        }
      },
      invalidatesTags: ["Notifications"],
    }),
    updateUser: builder.mutation({
      query: ({
        username,
        email,
        phone_number,
        id,
        password,
      }: {
        username: string;
        email: string;
        phone_number: string;
        id: string;
        password: string;
      }) => {
        return {
          url: "/user/" + id + "/update",
          method: "PUT",
          body: { username, email, phone_number, password },
        };
      },
      transformResponse: (result: { message: string }) => result,
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
        } catch (error) {}
      },
      invalidatesTags: ["User"],
    }),
    deleteUser: builder.mutation({
      query: (args: { id: string; password: string }) => {
        return {
          url: "/user/" + args.id + "/delete",
          method: "DELETE",
          body: { password: args.password },
        };
      },
      transformResponse: (result: { message: string }) => result,
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
        } catch (error) {}
      },
      invalidatesTags: ["User"],
    }),
    updateUserSettings: builder.mutation({
      query: (settings: UserSettings) => {
        return {
          url: "/user/updateSettings",
          method: "PUT",
          body: { ...settings },
        };
      },
      transformResponse: (result: { message: string }) => result,
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(updateUserSettings(args));
          localStorage.setItem("language", args.language);
        } catch (error) {}
      },
    }),
  }),
});

export const {
  useGetUserInfoQuery,
  useGetUserNotificationsQuery,
  useGetGroupUserInfoQuery,
  useToggleUserNotificationReadMutation,
  useReadAllNotificationsMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useUpdateUserSettingsMutation,
} = userAPI;
